/**
 * Mega-menu catalog for the AdminClient TopBar.
 *
 * Built on top of the existing `SECTION_NAV_ITEMS` from contexts.ts so we have
 * a single source of truth for routes. The mega-menu surfaces *flat* tiles per
 * group — when a section item has nested `children`, we promote those children
 * to top-level tiles to keep discovery one-click. The deep tree remains
 * available in the sidebar as before.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Briefcase, Crown, Scale, Shield, Database, ClipboardCheck, BarChart3,
  GraduationCap, Settings,
} from 'lucide-react'

import {
  SECTION_NAV_ITEMS,
  getVisibleSections,
  type GrcContextId,
  type NavItem,
  type NavSectionId,
} from '@/config/contexts'
import { tn } from '@/i18n/navLabels'
import type { MegaMenuGroup, MegaMenuTile } from '@/components/layout/MegaMenuGrid'

// ─── Section → accent color ────────────────────────────────────────────────

const SECTION_ACCENT: Partial<Record<NavSectionId, MegaMenuGroup['accent']>> = {
  portefeuille: 'amber',
  prospection: 'amber',
  revenus: 'amber',
  'strategie-cabinet': 'amber',
  'dashboards-exec': 'amber',
  'mes-mandats': 'blue',
  clients: 'blue',
  planification: 'blue',
  'suivi-qualite': 'blue',
  gouvernance: 'teal',
  conformite: 'teal',
  risques: 'orange',
  'mes-missions': 'blue',
  ethique: 'green',
  fournisseurs: 'indigo',
  securite: 'red',
  continuite: 'red',
  'referentiels-secu': 'red',
  donnees: 'purple',
  'ia-responsable': 'purple',
  'conformite-privacy': 'violet',
  'audit-planification': 'cyan',
  'audit-execution': 'cyan',
  'audit-certification': 'cyan',
  evaluations: 'indigo',
  'evaluation-360': 'teal',
  maturite: 'indigo',
  'dashboards-analyste': 'indigo',
  changement: 'green',
  formation: 'green',
  'ethique-formation': 'green',
  workspace: 'slate',
  rapports: 'slate',
  'mon-cabinet': 'slate',
  equipe: 'slate',
  facturation: 'slate',
  integrations: 'slate',
  juridique: 'slate',
  parametres: 'slate',
}

const SECTION_LABEL: Record<NavSectionId, string> = {
  portefeuille: 'Portefeuille',
  prospection: 'Prospection',
  revenus: 'Revenus',
  'strategie-cabinet': 'Stratégie Cabinet',
  'dashboards-exec': 'Dashboards Exec.',
  'mes-mandats': 'Mes Mandats',
  clients: 'Clients',
  planification: 'Planification',
  'suivi-qualite': 'Suivi & Qualité',
  gouvernance: 'Gouvernance',
  conformite: 'Conformité',
  risques: 'Risques',
  'mes-missions': 'Mes Missions',
  ethique: 'Éthique & ESG',
  fournisseurs: 'Fournisseurs',
  securite: 'Sécurité',
  continuite: 'Continuité',
  'referentiels-secu': 'Référentiels Sécu.',
  donnees: 'Données',
  'ia-responsable': 'IA Responsable',
  'conformite-privacy': 'Privacy / RGPD',
  'audit-planification': 'Plan. Audit',
  'audit-execution': 'Exéc. Audit',
  'audit-certification': 'Certification',
  evaluations: 'Évaluations',
  'evaluation-360': 'Évaluation & 360°',
  maturite: 'Maturité',
  'dashboards-analyste': 'Dashboards Analyste',
  changement: 'Changement',
  formation: 'Formation',
  'ethique-formation': 'Éthique & Culture',
  workspace: 'Workspace',
  rapports: 'Rapports',
  'ma-page': 'Ma Page',
  'mon-cabinet': 'Mon Cabinet',
  equipe: 'Équipe',
  facturation: 'Facturation',
  integrations: 'Intégrations',
  aigov: 'AIGOV360 Veille',
  juridique: 'Juridique',
  parametres: 'Paramètres',
  'copilot-associe': 'Copilot Associé',
  'copilot-directeur': 'Copilot Directeur',
  'copilot-consultant': 'Copilot Consultant',
  'copilot-securite': 'Copilot Sécurité',
  'copilot-donnees': 'Copilot Données',
  'copilot-auditeur': 'Copilot Auditeur',
  'copilot-analyste': 'Copilot Analyste',
  'copilot-formateur': 'Copilot Formateur',
  'rendez-vous': 'Rendez-vous',
  communaute: 'Communauté',
  'aide-transverse': 'Outils & Assistance',
}

// ─── Section → icon (group header icon) ────────────────────────────────────

const SECTION_ICON: Partial<Record<NavSectionId, LucideIcon>> = {
  portefeuille: Crown,
  'mes-mandats': Briefcase,
  'mes-missions': Briefcase,
  gouvernance: Scale,
  securite: Shield,
  donnees: Database,
  'audit-execution': ClipboardCheck,
  evaluations: BarChart3,
  formation: GraduationCap,
  parametres: Settings,
}

// ─── Per-context tile cap (avoid huge mega menus) ──────────────────────────

const MAX_TILES_PER_GROUP = 8

// ─── Flatten one NavItem into one or more tiles ────────────────────────────
//
// Rule: if the item has an `href`, emit the item itself as a tile. If it has
// `children` (with hrefs) and no own href, emit each child as a tile.
// Children whose own children exist (3-deep) are flattened by leading title.
function flattenItemToTiles(item: NavItem): MegaMenuTile[] {
  if (item.href) {
    return [{ label: tn(item.title), icon: item.icon, href: item.href }]
  }
  if (item.children && item.children.length > 0) {
    const out: MegaMenuTile[] = []
    for (const child of item.children) {
      if (child.href) {
        out.push({ label: tn(child.title), icon: child.icon, href: child.href })
      } else if (child.children) {
        for (const grand of child.children) {
          if (grand.href) {
            out.push({ label: tn(grand.title), icon: grand.icon, href: grand.href })
          }
        }
      }
    }
    return out
  }
  return []
}

function sectionToGroup(sectionId: NavSectionId): MegaMenuGroup | null {
  const items = SECTION_NAV_ITEMS[sectionId]
  if (!items || items.length === 0) return null

  // Build tiles (flatten 1–2 levels), then cap to keep the grid tidy.
  const tiles = items.flatMap(flattenItemToTiles).slice(0, MAX_TILES_PER_GROUP)
  if (tiles.length === 0) return null

  return {
    id: sectionId,
    label: tn(SECTION_LABEL[sectionId] ?? sectionId),
    icon: SECTION_ICON[sectionId],
    accent: SECTION_ACCENT[sectionId] ?? 'slate',
    tiles,
  }
}

/**
 * Build the mega-menu groups for a given context.
 * - Drops `copilot-*` sections from the menu (they live elsewhere) to keep
 *   the launcher focused on operational pages.
 * - Caps the menu to ~6 groups so the popover fits a normal viewport.
 */
export function getMegaMenuGroupsForContext(contextId: GrcContextId): MegaMenuGroup[] {
  const visible = getVisibleSections(contextId)
  const operational = visible.filter((s) => !s.startsWith('copilot-'))
  const groups: MegaMenuGroup[] = []
  for (const sectionId of operational) {
    const g = sectionToGroup(sectionId)
    if (g) groups.push(g)
    if (groups.length >= 6) break
  }
  return groups
}
